import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './RelationshipGraph.css';

/*
 * RelationshipGraph – pure SVG entity-relationship diagram.
 *
 * Props
 * ─────
 *   entities : Array<{ name, fields:[], relationships:[] }>
 *   colors   : optional array of hex colors for entity boxes
 */

const DEFAULT_COLORS = [
  '#6366f1', '#0f766e', '#b45309', '#475569',
  '#e11d48', '#0284c7', '#7c3aed', '#059669',
];

/* ── layout constants ── */
const BOX_W       = 240;

/* default SQL type map for physical view fallback */
const SQL_TYPES = {
  uuid: 'CHAR(36)', int: 'INTEGER', auto_increment: 'INTEGER',
  string: 'VARCHAR(255)', text: 'TEXT', sentence: 'VARCHAR(500)', word: 'VARCHAR(50)', slug: 'VARCHAR(200)',
  float: 'FLOAT', decimal: 'DECIMAL(10,2)', percentage: 'DECIMAL(5,2)',
  name: 'VARCHAR(100)', first_name: 'VARCHAR(50)', last_name: 'VARCHAR(50)',
  username: 'VARCHAR(50)', email: 'VARCHAR(254)', phone: 'VARCHAR(20)',
  age: 'SMALLINT', gender: 'VARCHAR(10)', job_title: 'VARCHAR(100)', ssn: 'CHAR(11)',
  company_name: 'VARCHAR(200)', product_name: 'VARCHAR(200)', store_name: 'VARCHAR(200)',
  brand_name: 'VARCHAR(100)', department: 'VARCHAR(100)', industry: 'VARCHAR(100)',
  sku: 'VARCHAR(30)', price: 'DECIMAL(10,2)', currency_code: 'CHAR(3)',
  credit_card: 'VARCHAR(19)', iban: 'VARCHAR(34)',
  hospital_name: 'VARCHAR(200)', drug_name: 'VARCHAR(150)', diagnosis: 'VARCHAR(200)', blood_type: 'VARCHAR(5)',
  address: 'VARCHAR(300)', street: 'VARCHAR(200)', city: 'VARCHAR(100)',
  state: 'VARCHAR(100)', country: 'VARCHAR(60)', zip_code: 'VARCHAR(10)',
  latitude: 'DECIMAL(9,6)', longitude: 'DECIMAL(9,6)',
  timestamp: 'TIMESTAMP', date: 'DATE', time: 'TIME', date_of_birth: 'DATE', year: 'SMALLINT',
  url: 'VARCHAR(2048)', domain: 'VARCHAR(253)', ipv4: 'VARCHAR(15)', ipv6: 'VARCHAR(45)',
  mac_address: 'CHAR(17)', user_agent: 'VARCHAR(500)',
  boolean: 'BOOLEAN', color: 'VARCHAR(30)', hex_color: 'CHAR(7)',
  file_name: 'VARCHAR(255)', mime_type: 'VARCHAR(127)', json: 'JSON',
};
const FIELD_H     = 22;
const HEADER_H    = 34;
const PK_LABEL_H  = 4;           /* small top padding inside PK zone */
const PK_PAD      = 4;           /* padding below last PK field / empty slot */
const PK_EMPTY_H  = 22;          /* drop-target height when no PKs exist */
const PAD_X       = 80;          /* horizontal gap between boxes  */
const PAD_Y       = 60;          /* vertical gap between boxes    */
const CANVAS_PAD  = 40;          /* padding around the whole SVG  */
const MIN_GAP     = 50;          /* minimum clear space between any two boxes */

/* PK zone height helper */
function pkZoneH(ent) {
  const pkCount = (ent.fields || []).filter(f => f.primary_key).length;
  return PK_LABEL_H + (pkCount > 0 ? pkCount * FIELD_H : PK_EMPTY_H) + PK_PAD;
}

/* total box height helper */
function boxH(ent) {
  const fields = ent.fields || [];
  const nonPkCount = fields.filter(f => !f.primary_key).length;
  return HEADER_H + pkZoneH(ent) + Math.max(1, nonPkCount) * FIELD_H + 10;
}

function layoutEntities(entities) {
  /*
   * Star / radial layout:
   *   – The entity with the most relationships is placed at the centre.
   *   – All other entities are arranged in a circle around it.
   *   – The radius is computed so adjacent boxes never overlap.
   *   – Falls back to a simple line for ≤ 2 entities.
   */
  if (entities.length === 0) return { positions: [], width: 400, height: 200 };

  const positions = [];

  /* ── Score each entity by total relationship count (as child or parent) ── */
  const relCounts = entities.map((ent, i) => {
    let count = (ent.relationships || []).length;
    entities.forEach((other, j) => {
      if (j === i) return;
      (other.relationships || []).forEach(r => {
        if (r.parent_entity === ent.name) count++;
      });
    });
    return count;
  });

  const centerIdx = relCounts.indexOf(Math.max(...relCounts));

  /* ── Single entity ── */
  if (entities.length === 1) {
    const h = boxH(entities[0]);
    positions.push({ x: CANVAS_PAD, y: CANVAS_PAD, w: BOX_W, h, entity: entities[0], index: 0 });
    return { positions, width: BOX_W + CANVAS_PAD * 2, height: h + CANVAS_PAD * 2 };
  }

  /* ── Two entities: side by side with generous gap ── */
  if (entities.length === 2) {
    entities.forEach((ent, i) => {
      const h = boxH(ent);
      positions.push({
        x: CANVAS_PAD + i * (BOX_W + PAD_X),
        y: CANVAS_PAD,
        w: BOX_W, h, entity: ent, index: i,
      });
    });
    const maxH = Math.max(...positions.map(p => p.h));
    return {
      positions,
      width:  CANVAS_PAD * 2 + 2 * BOX_W + PAD_X,
      height: maxH + CANVAS_PAD * 2,
    };
  }

  /* ── Star layout for 3+ entities ── */
  const n = entities.length - 1;                     /* outer count */

  /*
   * Compute the minimum radius so that adjacent outer boxes
   * have at least MIN_GAP pixels between them.
   *
   * Two adjacent boxes are separated by angle θ = 2π/n.
   * The chord length between their centres = 2·R·sin(θ/2).
   * We want chord ≥ max(BOX_W, tallest_box_h) + MIN_GAP.
   */
  const outerHeights = entities.filter((_, i) => i !== centerIdx).map(boxH);
  const maxBoxDim = Math.max(BOX_W, ...outerHeights);
  const requiredChord = maxBoxDim + MIN_GAP;

  const halfAngle = Math.PI / n;                     /* sin(θ/2) */
  const sinHalf = Math.sin(halfAngle);
  const radiusFromChord = sinHalf > 0.001 ? requiredChord / (2 * sinHalf) : 400;

  /* Also ensure the radius is big enough to not overlap the center box */
  const centerH = boxH(entities[centerIdx]);
  const radiusFromCenter = (maxBoxDim + centerH) / 2 + MIN_GAP;

  const RADIUS = Math.max(radiusFromChord, radiusFromCenter, 200);

  /* Centre of the star */
  const cx = CANVAS_PAD + RADIUS + BOX_W / 2;
  const cy = CANVAS_PAD + RADIUS + centerH / 2;

  /* Place centre entity */
  positions[centerIdx] = {
    x: cx - BOX_W / 2,
    y: cy - centerH / 2,
    w: BOX_W, h: centerH,
    entity: entities[centerIdx], index: centerIdx,
  };

  /* Place outer entities around the circle starting from top */
  const startAngle = -Math.PI / 2;
  let outerIdx = 0;
  entities.forEach((ent, i) => {
    if (i === centerIdx) return;
    const angle = startAngle + (2 * Math.PI * outerIdx) / n;
    const h = boxH(ent);
    const ex = cx + RADIUS * Math.cos(angle) - BOX_W / 2;
    const ey = cy + RADIUS * Math.sin(angle) - h / 2;
    positions[i] = { x: ex, y: ey, w: BOX_W, h, entity: ent, index: i };
    outerIdx++;
  });

  /* ── Normalise: shift everything so nothing has a negative coordinate ── */
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  positions.forEach(p => {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x + p.w > maxX) maxX = p.x + p.w;
    if (p.y + p.h > maxY) maxY = p.y + p.h;
  });

  const shiftX = minX < CANVAS_PAD ? CANVAS_PAD - minX : 0;
  const shiftY = minY < CANVAS_PAD ? CANVAS_PAD - minY : 0;
  positions.forEach(p => { p.x += shiftX; p.y += shiftY; });

  const totalW = maxX + shiftX + CANVAS_PAD;
  const totalH = maxY + shiftY + CANVAS_PAD;

  return { positions, width: Math.max(totalW, 400), height: Math.max(totalH, 200) };
}

/* find the connection point for a field on an entity box */
function fieldAnchor(pos, fieldName, side) {
  const fields = pos.entity.fields || [];
  const f = fields.find(ff => ff.name === fieldName);
  if (!f) {
    return { x: side === 'right' ? pos.x + pos.w : pos.x, y: pos.y + HEADER_H + FIELD_H / 2 };
  }
  const pkFields = fields.filter(ff => ff.primary_key);
  const nonPkFields = fields.filter(ff => !ff.primary_key);
  const pzH = pkZoneH(pos.entity);
  let y;
  if (f.primary_key) {
    const pkIdx = pkFields.indexOf(f);
    y = pos.y + HEADER_H + PK_LABEL_H + pkIdx * FIELD_H + FIELD_H / 2;
  } else {
    const npIdx = nonPkFields.indexOf(f);
    y = pos.y + HEADER_H + pzH + npIdx * FIELD_H + FIELD_H / 2;
  }
  const x = side === 'right' ? pos.x + pos.w : pos.x;
  return { x, y };
}

/* build a nice curved path between two points */
function curvePath(x1, y1, x2, y2) {
  const dx = Math.abs(x2 - x1);
  const cp = Math.max(40, dx * 0.4);
  const sx = x1 < x2 ? 1 : -1;
  return `M${x1},${y1} C${x1 + cp * sx},${y1} ${x2 - cp * sx},${y2} ${x2},${y2}`;
}

/* cardinality symbol: returns [sourceLabel, targetLabel] */
function cardLabels(cardinality) {
  switch (cardinality) {
    case 'one-to-one':   return ['1', '1'];
    case 'one-to-many':  return ['1', 'N'];
    case 'many-to-one':  return ['N', '1'];
    case 'many-to-many': return ['N', 'N'];
    default:             return ['', ''];
  }
}

export default function RelationshipGraph({ entities = [], colors = DEFAULT_COLORS, title = 'ER Diagram', onAddRelationship, onDeleteRelationship, onTogglePrimaryKey, onReorderFields }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [hoveredRel, setHoveredRel] = useState(null);

  /* ── Logical / Physical view toggle ── */
  const [viewMode, setViewMode] = useState('logical'); // 'logical' | 'physical'

  /* ── PK drag state (drag field into/out of PK zone) ── */
  const [pkDrag, setPkDrag] = useState(null);
  // { entityName, fieldName, isPK, startY, curY }
  const pkDragRef = useRef(null); // mirrors pkDrag for synchronous reads in mouseUp

  /* ── Draggable positions state ────────────────────────── */
  const initialLayout = useMemo(() => layoutEntities(entities), [entities]);

  const [positions, setPositions] = useState(initialLayout.positions);
  const [canvasSize, setCanvasSize] = useState({ width: initialLayout.width, height: initialLayout.height });

  /* ── Pan state (viewBox offset) ── */
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

  /* ── Field connector-drag state ── */
  const [connectorDrag, setConnectorDrag] = useState(null);
  // { srcEntityName, srcFieldName, srcX, srcY, curX, curY }
  const [connectorTarget, setConnectorTarget] = useState(null);
  // { entityName, fieldName } — the field currently hovered during connector drag
  const connectorTargetRef = useRef(null); // ref mirror so mouseUp can read synchronously

  // Re-sync when entities prop changes
  const prevEntitiesRef = useRef(entities);
  useEffect(() => {
    if (prevEntitiesRef.current !== entities) {
      const prevNames = (prevEntitiesRef.current || []).map(e => e.name).join(',');
      const currNames = (entities || []).map(e => e.name).join(',');
      prevEntitiesRef.current = entities;

      if (prevNames !== currNames) {
        // Entity list changed (added/removed) → full re-layout
        const layout = layoutEntities(entities);
        setPositions(layout.positions);
        setCanvasSize({ width: layout.width, height: layout.height });
        setPanOffset({ x: 0, y: 0 });
      } else {
        // Only entity contents changed (e.g. new relationship) → update entity refs in place
        setPositions(prev => prev.map((p, i) => ({
          ...p,
          entity: entities[i] || p.entity,
        })));
      }
    }
  }, [entities]);

  /* ── Interaction refs ── */
  const dragRef = useRef(null);       // entity drag:   { index, startX, startY, origPosX, origPosY }
  const panRef = useRef(null);        // pan drag:      { startX, startY, origPanX, origPanY }
  const connDragRef = useRef(null);   // connector drag: { srcEntityName, srcFieldName, anchorX, anchorY }

  const getSvgPoint = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: clientX, y: clientY };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: clientX, y: clientY };
    const svgPt = pt.matrixTransform(ctm.inverse());
    return { x: svgPt.x, y: svgPt.y };
  }, []);

  /* ── Entity drag start (from header only) ── */
  const onEntityDragStart = useCallback((e, idx) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = getSvgPoint(e.clientX, e.clientY);
    dragRef.current = {
      index: idx,
      startX: pt.x,
      startY: pt.y,
      origPosX: positions[idx].x,
      origPosY: positions[idx].y,
    };
    if (containerRef.current) containerRef.current.classList.add('rg-dragging');
  }, [positions, getSvgPoint]);

  /* ── Pan start (from SVG background) ── */
  const onPanStart = useCallback((e) => {
    // Only start pan on background clicks (not on entities/fields)
    if (e.button !== 0) return;
    // Check if the click target is the SVG itself or the background
    if (e.target !== svgRef.current && !e.target.classList?.contains('rg-bg')) return;
    e.preventDefault();
    const pt = getSvgPoint(e.clientX, e.clientY);
    panRef.current = {
      startX: pt.x,
      startY: pt.y,
      origPanX: panOffset.x,
      origPanY: panOffset.y,
    };
    if (containerRef.current) containerRef.current.classList.add('rg-panning');
  }, [panOffset, getSvgPoint]);

  /* ── Field connector drag start ── */
  const onFieldDragStart = useCallback((e, entityName, fieldName, anchorX, anchorY) => {
    if (e.button !== 0 || !onAddRelationship) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = getSvgPoint(e.clientX, e.clientY);
    connDragRef.current = { srcEntityName: entityName, srcFieldName: fieldName, anchorX, anchorY };
    setConnectorDrag({
      srcEntityName: entityName,
      srcFieldName: fieldName,
      srcX: anchorX,
      srcY: anchorY,
      curX: pt.x,
      curY: pt.y,
    });
    if (containerRef.current) containerRef.current.classList.add('rg-connecting');
  }, [onAddRelationship, getSvgPoint]);

  /* ── Unified mouse-move / mouse-up ── */
  useEffect(() => {
    const handleMouseMove = (e) => {
      const pt = getSvgPoint(e.clientX, e.clientY);

      /* Entity drag */
      if (dragRef.current) {
        const dx = pt.x - dragRef.current.startX;
        const dy = pt.y - dragRef.current.startY;
        const idx = dragRef.current.index;
        setPositions(prev => {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            x: dragRef.current.origPosX + dx,
            y: dragRef.current.origPosY + dy,
          };
          return next;
        });
        return;
      }

      /* Pan drag */
      if (panRef.current) {
        const dx = pt.x - panRef.current.startX;
        const dy = pt.y - panRef.current.startY;
        setPanOffset({
          x: panRef.current.origPanX - dx,
          y: panRef.current.origPanY - dy,
        });
        return;
      }

      /* Connector drag */
      if (connDragRef.current) {
        setConnectorDrag(prev => prev ? { ...prev, curX: pt.x, curY: pt.y } : null);
        return;
      }

      /* PK drag (vertical drag to promote/demote + reorder) */
      if (pkDragRef.current) {
        pkDragRef.current.curY = pt.y; // keep ref in sync for mouseUp
        // Compute insert index for within-zone reorder
        const d = pkDragRef.current;
        const baseY = d.zone === 'pk'
          ? d.pkZoneTop + PK_LABEL_H
          : d.pkZoneBottom;
        const relY = pt.y - baseY;
        const insertIdx = Math.max(0, Math.min(d.zoneFieldCount, Math.round(relY / FIELD_H)));
        d.insertIndex = insertIdx;
        setPkDrag(prev => prev ? { ...prev, curY: pt.y, insertIndex: insertIdx } : null);
        return;
      }
    };

    const handleMouseUp = () => {
      /* PK drag end — toggle PK if zone crossed, or reorder if same zone */
      if (pkDragRef.current) {
        const d = pkDragRef.current;
        const curY = d.curY;
        pkDragRef.current = null;
        if (containerRef.current) containerRef.current.classList.remove('rg-pk-dragging-mode');

        // Cross-zone: toggle PK
        if (onTogglePrimaryKey) {
          if (d.isPK && curY > d.pkZoneBottom) {
            // Was PK, dragged below PK zone → demote
            onTogglePrimaryKey({ entityName: d.entityName, fieldName: d.fieldName, isPK: false });
            setPkDrag(null);
            return;
          } else if (!d.isPK && curY >= d.pkZoneTop && curY <= d.pkZoneBottom) {
            // Was not PK, dragged into PK zone → promote
            onTogglePrimaryKey({ entityName: d.entityName, fieldName: d.fieldName, isPK: true });
            setPkDrag(null);
            return;
          }
        }

        // Within-zone: reorder
        if (onReorderFields && d.insertIndex !== undefined &&
            d.insertIndex !== d.fieldIndex && d.insertIndex !== d.fieldIndex + 1) {
          const entity = entities.find(e => e.name === d.entityName);
          if (entity) {
            const pkF = (entity.fields || []).filter(f => f.primary_key);
            const nonPkF = (entity.fields || []).filter(f => !f.primary_key);
            const zoneFields = d.zone === 'pk' ? [...pkF] : [...nonPkF];

            // Remove from current position and insert at new position
            const [moved] = zoneFields.splice(d.fieldIndex, 1);
            const targetIdx = d.insertIndex > d.fieldIndex ? d.insertIndex - 1 : d.insertIndex;
            zoneFields.splice(targetIdx, 0, moved);

            // Reconstruct full fields array: PK fields first, then non-PK
            const newFields = d.zone === 'pk'
              ? [...zoneFields, ...nonPkF]
              : [...pkF, ...zoneFields];

            onReorderFields({ entityName: d.entityName, fields: newFields });
          }
        }

        setPkDrag(null);
        return;
      }

      /* Entity drag end */
      if (dragRef.current) {
        dragRef.current = null;
        if (containerRef.current) containerRef.current.classList.remove('rg-dragging');
        setPositions(prev => {
          let maxX = 0, maxY = 0;
          prev.forEach(p => {
            if (p.x + p.w + CANVAS_PAD > maxX) maxX = p.x + p.w + CANVAS_PAD;
            if (p.y + p.h + CANVAS_PAD > maxY) maxY = p.y + p.h + CANVAS_PAD;
          });
          setCanvasSize({ width: Math.max(maxX, 400), height: Math.max(maxY, 200) });
          return prev;
        });
        return;
      }

      /* Pan end */
      if (panRef.current) {
        panRef.current = null;
        if (containerRef.current) containerRef.current.classList.remove('rg-panning');
        return;
      }

      /* Connector drag end — create relationship if target is valid */
      if (connDragRef.current) {
        const src = connDragRef.current;
        const tgt = connectorTargetRef.current; // read ref synchronously (immune to onMouseLeave race)
        connDragRef.current = null;
        connectorTargetRef.current = null;
        if (containerRef.current) containerRef.current.classList.remove('rg-connecting');

        if (tgt && tgt.entityName !== src.srcEntityName && onAddRelationship) {
          onAddRelationship({
            childEntity: src.srcEntityName,
            childField: src.srcFieldName,
            parentEntity: tgt.entityName,
            parentField: tgt.fieldName,
            cardinality: 'many-to-one',
            required: false,
          });
        }
        setConnectorTarget(null);
        setConnectorDrag(null);
        return;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [getSvgPoint, onAddRelationship, onTogglePrimaryKey, onReorderFields, entities]);

  /* ── Reset layout ── */
  const resetLayout = useCallback(() => {
    const layout = layoutEntities(entities);
    setPositions(layout.positions);
    setCanvasSize({ width: layout.width, height: layout.height });
    setPanOffset({ x: 0, y: 0 });
  }, [entities]);

  /* ── Fit to window ── */
  const fitToWindow = useCallback(() => {
    const container = containerRef.current;
    if (!container || positions.length === 0) return;

    const availW = container.clientWidth - 24;
    const availH = Math.max(container.clientHeight - 60, 300);

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    positions.forEach(p => {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x + p.w > maxX) maxX = p.x + p.w;
      if (p.y + p.h > maxY) maxY = p.y + p.h;
    });

    const contentW = maxX - minX;
    const contentH = maxY - minY;
    if (contentW <= 0 || contentH <= 0) return;

    const scale = Math.min(1, (availW - CANVAS_PAD * 2) / contentW, (availH - CANVAS_PAD * 2) / contentH);

    setPositions(prev => prev.map(p => ({
      ...p,
      x: CANVAS_PAD + (p.x - minX) * scale,
      y: CANVAS_PAD + (p.y - minY) * scale,
      w: p.w * scale,
      h: p.h * scale,
    })));

    setCanvasSize({
      width:  Math.max(CANVAS_PAD * 2 + contentW * scale, availW),
      height: Math.max(CANVAS_PAD * 2 + contentH * scale, 200),
    });
    setPanOffset({ x: 0, y: 0 });
  }, [positions]);

  /* ── Compute edges from live positions ── */
  const edges = useMemo(() => {
    const edgeList = [];
    positions.forEach((p) => {
      (p.entity.relationships || []).forEach((rel, rIdx) => {
        const targetPos = positions.find(pp => pp.entity.name === rel.parent_entity);
        if (!targetPos) return;

        const srcRight = p.x < targetPos.x;
        const src = fieldAnchor(p, rel.child_field, srcRight ? 'right' : 'left');
        const tgt = fieldAnchor(targetPos, rel.parent_field, srcRight ? 'left' : 'right');
        const [srcLabel, tgtLabel] = cardLabels(rel.cardinality);

        edgeList.push({
          id: `${p.index}-${rIdx}`,
          entityIndex: p.index,
          relIndex: rIdx,
          path: curvePath(src.x, src.y, tgt.x, tgt.y),
          sx: src.x, sy: src.y,
          tx: tgt.x, ty: tgt.y,
          srcLabel, tgtLabel,
          srcEntity: p.entity.name,
          tgtEntity: rel.parent_entity,
          childField: rel.child_field,
          parentField: rel.parent_field,
          cardinality: rel.cardinality,
          required: rel.required,
          color: colors[p.index % colors.length],
        });
      });
    });
    return edgeList;
  }, [positions, colors]);
  const getSvgString = () => {
    const svg = svgRef.current;
    if (!svg) return null;

    /* ── Recompute a fresh star layout so export is always clean ── */
    const exportLayout = layoutEntities(entities);
    const exportPositions = exportLayout.positions;

    const bg          = '#ffffff';
    const textPrimary = '#111827';
    const textMuted   = '#6b7280';
    const border      = '#e5e7eb';

    /* Tight bounding box around all exported entity boxes */
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    exportPositions.forEach(p => {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + p.w);
      maxY = Math.max(maxY, p.y + p.h);
    });
    const pad   = CANVAS_PAD;
    const vx    = minX - pad;
    const vy    = minY - pad;
    const vw    = (maxX - minX) + pad * 2;
    const vh    = (maxY - minY) + pad * 2;

    /* Build the SVG markup directly from exportPositions (not the live DOM) */
    const svgNS = 'http://www.w3.org/2000/svg';
    const exportSvg = document.createElementNS(svgNS, 'svg');
    exportSvg.setAttribute('xmlns', svgNS);
    exportSvg.setAttribute('viewBox', `${vx} ${vy} ${vw} ${vh}`);
    exportSvg.setAttribute('width',  String(vw));
    exportSvg.setAttribute('height', String(vh));

    /* Font style */
    const style = document.createElementNS(svgNS, 'style');
    style.textContent = 'text { font-family: system-ui, -apple-system, sans-serif; font-size: 11px; }';
    exportSvg.appendChild(style);

    /* Defs: arrow marker */
    const defs = document.createElementNS(svgNS, 'defs');
    defs.innerHTML = `<marker id="rg-arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill="none" stroke="#6366f1" stroke-width="1.2"/></marker>`;
    exportSvg.appendChild(defs);

    /* White background */
    const bgRect = document.createElementNS(svgNS, 'rect');
    bgRect.setAttribute('x', String(vx));
    bgRect.setAttribute('y', String(vy));
    bgRect.setAttribute('width',  String(vw));
    bgRect.setAttribute('height', String(vh));
    bgRect.setAttribute('fill', bg);
    exportSvg.appendChild(bgRect);

    const mkEl = (tag, attrs = {}, parent) => {
      const el = document.createElementNS(svgNS, tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, String(v)));
      if (parent) parent.appendChild(el);
      return el;
    };
    const mkText = (parent, txt, attrs = {}) => {
      const el = mkEl('text', attrs, parent);
      el.textContent = txt;
      return el;
    };

    /* Draw edges first (behind boxes) */
    entities.forEach((ent, ei) => {
      const ep = exportPositions[ei];
      if (!ep) return;
      (ent.relationships || []).forEach((rel) => {
        const tp = exportPositions.find(pp => pp.entity.name === rel.parent_entity);
        if (!tp) return;
        const srcRight = ep.x < tp.x;
        const src = fieldAnchor(ep, rel.child_field, srcRight ? 'right' : 'left');
        const tgt = fieldAnchor(tp, rel.parent_field, srcRight ? 'left' : 'right');
        const [srcLabel, tgtLabel] = cardLabels(rel.cardinality);
        mkEl('path', {
          d: curvePath(src.x, src.y, tgt.x, tgt.y),
          fill: 'none', stroke: '#a5b4fc', 'stroke-width': '1.3',
          'stroke-dasharray': rel.required ? 'none' : '5,3',
          'marker-end': 'url(#rg-arrow)',
        }, exportSvg);
        mkText(exportSvg, srcLabel, { x: src.x + (src.x < tgt.x ? 10 : -10), y: src.y - 6, 'text-anchor': src.x < tgt.x ? 'start' : 'end', fill: '#6366f1', 'font-size': '10', 'font-weight': '600' });
        mkText(exportSvg, tgtLabel, { x: tgt.x + (tgt.x > src.x ? -10 : 10), y: tgt.y - 6, 'text-anchor': tgt.x > src.x ? 'end' : 'start', fill: '#6366f1', 'font-size': '10', 'font-weight': '600' });
      });
    });

    /* Draw entity boxes */
    exportPositions.forEach((p, idx) => {
      const color  = colors[idx % colors.length];
      const ent    = p.entity;
      const fields = ent.fields || [];
      const pkF    = fields.filter(f => f.primary_key);
      const nonPkF = fields.filter(f => !f.primary_key);
      const pzH    = pkZoneH(ent);

      /* Shadow */
      mkEl('rect', { x: p.x+2, y: p.y+2, width: p.w, height: p.h, rx: 8, fill: 'rgba(0,0,0,0.06)' }, exportSvg);
      /* Box border */
      mkEl('rect', { x: p.x, y: p.y, width: p.w, height: p.h, rx: 8, fill: bg, stroke: color, 'stroke-width': '1.2' }, exportSvg);
      /* Header */
      mkEl('rect', { x: p.x, y: p.y, width: p.w, height: HEADER_H, rx: 8, fill: color }, exportSvg);
      mkEl('rect', { x: p.x, y: p.y + HEADER_H - 8, width: p.w, height: 8, fill: color }, exportSvg);
      /* Entity name */
      mkText(exportSvg, ent.name, { x: p.x + p.w/2, y: p.y + HEADER_H/2 + 4, 'text-anchor': 'middle', fill: '#fff', 'font-weight': '600', 'font-size': '12' });

      /* PK zone bg */
      const pkZoneTop = p.y + HEADER_H;
      mkEl('rect', { x: p.x+4, y: pkZoneTop+2, width: p.w-8, height: pzH-4, rx: 6, fill: 'rgba(251,191,36,.07)', stroke: 'rgba(180,83,9,.18)', 'stroke-width': '0.7' }, exportSvg);

      /* PK fields */
      pkF.forEach((f, fi) => {
        const fy = pkZoneTop + PK_LABEL_H + fi * FIELD_H + FIELD_H / 2;
        if (fi > 0) mkEl('line', { x1: p.x+8, y1: fy-FIELD_H/2, x2: p.x+p.w-8, y2: fy-FIELD_H/2, stroke: border, 'stroke-width': '0.5' }, exportSvg);
        mkText(exportSvg, '[PK] ' + f.name, { x: p.x+10, y: fy+4, fill: '#92400e', 'font-weight': '600', 'font-size': '11' });
        mkText(exportSvg, f.sql_type || SQL_TYPES[f.type] || f.type || '', { x: p.x+p.w-10, y: fy+4, 'text-anchor': 'end', fill: textMuted, 'font-size': '10' });
      });

      /* Separator */
      const pkBottom = pkZoneTop + pzH;
      mkEl('line', { x1: p.x+8, y1: pkBottom-2, x2: p.x+p.w-8, y2: pkBottom-2, stroke: border, 'stroke-width': '0.5' }, exportSvg);

      /* Regular fields */
      nonPkF.forEach((f, fi) => {
        const fy = pkBottom + fi * FIELD_H + FIELD_H / 2;
        if (fi > 0) mkEl('line', { x1: p.x+8, y1: fy-FIELD_H/2, x2: p.x+p.w-8, y2: fy-FIELD_H/2, stroke: border, 'stroke-width': '0.5' }, exportSvg);
        mkText(exportSvg, f.name, { x: p.x+10, y: fy+4, fill: textPrimary, 'font-size': '11' });
        mkText(exportSvg, f.sql_type || SQL_TYPES[f.type] || f.type || '', { x: p.x+p.w-10, y: fy+4, 'text-anchor': 'end', fill: textMuted, 'font-size': '10' });
      });
    });

    exportSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    return new XMLSerializer().serializeToString(exportSvg);
  };

  /* Robust file-save helper */
  const saveFile = (blob, filename) => {
    // Try native saveAs (Edge legacy / IE)
    if (window.navigator?.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.position = 'fixed';
    a.style.left = '-9999px';
    document.body.appendChild(a);
    // Small delay ensures browser registers the element
    requestAnimationFrame(() => {
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 2000);
    });
  };

  const downloadSvg = () => {
    const svgStr = getSvgString();
    if (!svgStr) return;
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    saveFile(blob, `${title.replace(/[^a-zA-Z0-9_-]/g, '_')}_ER.svg`);
  };

  const downloadPdf = () => {
    const svgStr = getSvgString();
    if (!svgStr) return;

    /* Parse dimensions from the serialized SVG (set explicitly by getSvgString) */
    const dimMatch = svgStr.match(/width="(\d+(?:\.\d+)?)"[^>]*height="(\d+(?:\.\d+)?)"/);
    const svgW = dimMatch ? parseFloat(dimMatch[1]) : 800;
    const svgH = dimMatch ? parseFloat(dimMatch[2]) : 600;

    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width  = Math.ceil(svgW * scale);
    canvas.height = Math.ceil(svgH * scale);
    const ctx = canvas.getContext('2d');

    /* White background */
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);

    const img = new Image(svgW, svgH);
    img.onload = () => {
      ctx.drawImage(img, 0, 0, svgW, svgH);
      canvas.toBlob((blob) => {
        if (!blob) return;
        saveFile(blob, `${title.replace(/[^a-zA-Z0-9_-]/g, '_')}_ER.png`);
      }, 'image/png');
    };
    img.onerror = () => {
      downloadSvg();
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}`;
  };

  if (entities.length === 0) {
    return (
      <div className="rg-empty">
        <span>No entities to visualize.</span>
      </div>
    );
  }

  return (
    <div className="rg-container" ref={containerRef}>
      <div className="rg-view-toggle rg-view-toggle-floating" title="Switch between logical and physical types">
        <button
          className={`rg-toggle-btn ${viewMode === 'logical' ? 'rg-toggle-active' : ''}`}
          onClick={() => setViewMode('logical')}
        >
          Logical
        </button>
        <button
          className={`rg-toggle-btn ${viewMode === 'physical' ? 'rg-toggle-active' : ''}`}
          onClick={() => setViewMode('physical')}
        >
          Physical
        </button>
      </div>
      <svg
        ref={svgRef}
        className="rg-svg"
        viewBox={`${panOffset.x} ${panOffset.y} ${canvasSize.width} ${canvasSize.height}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseDown={onPanStart}
      >
        {/* Invisible background rect for pan click-target */}
        <rect
          className="rg-bg"
          x={panOffset.x} y={panOffset.y}
          width={canvasSize.width} height={canvasSize.height}
          fill="transparent"
        />

        <defs>
          <marker id="rg-arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <path d="M0,0 L8,3 L0,6" fill="none" stroke="#6366f1" strokeWidth="1.2" />
          </marker>
          <marker id="rg-arrow-hl" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <path d="M0,0 L8,3 L0,6" fill="none" stroke="#4f46e5" strokeWidth="1.5" />
          </marker>
        </defs>

        {/* Edges */}
        {edges.map((e) => {
          const hl = hoveredRel === e.id;
          return (
            <g
              key={e.id}
              className={`rg-edge ${hl ? 'rg-edge-hl' : ''}`}
              onMouseEnter={() => setHoveredRel(e.id)}
              onMouseLeave={() => setHoveredRel(null)}
            >
              <path d={e.path} fill="none" stroke="transparent" strokeWidth="14" />
              <path
                d={e.path}
                fill="none"
                stroke={hl ? '#4f46e5' : '#a5b4fc'}
                strokeWidth={hl ? 2 : 1.3}
                strokeDasharray={e.required ? 'none' : '5,3'}
                markerEnd={hl ? 'url(#rg-arrow-hl)' : 'url(#rg-arrow)'}
              />
              <text x={e.sx + (e.sx < e.tx ? 10 : -10)} y={e.sy - 6}
                className="rg-card-label" textAnchor={e.sx < e.tx ? 'start' : 'end'}>{e.srcLabel}</text>
              <text x={e.tx + (e.tx > e.sx ? -10 : 10)} y={e.ty - 6}
                className="rg-card-label" textAnchor={e.tx > e.sx ? 'end' : 'start'}>{e.tgtLabel}</text>
              {hl && (
                <text
                  x={(e.sx + e.tx) / 2}
                  y={(e.sy + e.ty) / 2 - 8}
                  className="rg-edge-tooltip"
                  textAnchor="middle"
                >
                  {e.srcEntity}.{e.childField} → {e.tgtEntity}.{e.parentField}
                </text>
              )}
              {hl && onDeleteRelationship && (
                <g
                  className="rg-edge-delete"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onDeleteRelationship({ entityName: e.srcEntity, relIndex: e.relIndex });
                    setHoveredRel(null);
                  }}
                >
                  <circle
                    cx={(e.sx + e.tx) / 2}
                    cy={(e.sy + e.ty) / 2 + 10}
                    r="9"
                    fill="#fee2e2"
                    stroke="#ef4444"
                    strokeWidth="1"
                  />
                  <text
                    x={(e.sx + e.tx) / 2}
                    y={(e.sy + e.ty) / 2 + 10}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="rg-edge-delete-icon"
                  >
                    ×
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Entity boxes */}
        {positions.map((p, idx) => {
          const color = colors[idx % colors.length];
          const allFields = p.entity.fields || [];
          const pkFields = allFields.filter(f => f.primary_key);
          const nonPkFields = allFields.filter(f => !f.primary_key);
          const pzH = pkZoneH(p.entity);
          const pkZoneTop = p.y + HEADER_H;
          const pkZoneBottom = pkZoneTop + pzH;

          /* helper to render a single field row */
          const renderField = (f, fy, fi, zone) => {
            const isFK = entities.some(ent =>
              (ent.relationships || []).some(r =>
                (r.child_field === f.name && ent.name === p.entity.name) ||
                (r.parent_field === f.name && r.parent_entity === p.entity.name)
              )
            );
            const isPK = f.primary_key === true;
            const isDropTarget = connectorDrag &&
              connectorTarget?.entityName === p.entity.name &&
              connectorTarget?.fieldName === f.name;
            const isSource = connectorDrag &&
              connectorDrag.srcEntityName === p.entity.name &&
              connectorDrag.srcFieldName === f.name;
            const isBeingDragged = pkDrag &&
              pkDrag.entityName === p.entity.name &&
              pkDrag.fieldName === f.name;

            return (
              <g
                key={`${zone}-${f.name}`}
                className={`rg-field-row ${onAddRelationship && !onTogglePrimaryKey && !onReorderFields ? 'rg-field-draggable' : ''} ${(onTogglePrimaryKey || onReorderFields) ? 'rg-field-reorderable' : ''} ${isDropTarget ? 'rg-field-drop-target' : ''} ${isSource ? 'rg-field-source' : ''} ${isBeingDragged ? 'rg-field-pk-dragging' : ''}`}
                onMouseDown={(e) => {
                  if (e.button !== 0) return;
                  if (onTogglePrimaryKey || onReorderFields) {
                    // Vertical drag: PK toggle (cross-zone) or reorder (within zone)
                    e.preventDefault();
                    e.stopPropagation();
                    const pt = getSvgPoint(e.clientX, e.clientY);
                    pkDragRef.current = {
                      entityName: p.entity.name,
                      fieldName: f.name,
                      isPK: isPK,
                      startY: pt.y,
                      curY: pt.y,
                      pkZoneTop,
                      pkZoneBottom,
                      entityIdx: idx,
                      zone: isPK ? 'pk' : 'nonPk',
                      fieldIndex: fi,
                      zoneFieldCount: isPK ? pkFields.length : nonPkFields.length,
                      insertIndex: fi,
                    };
                    setPkDrag({
                      entityName: p.entity.name,
                      fieldName: f.name,
                      isPK: isPK,
                      startY: pt.y,
                      curY: pt.y,
                      zone: isPK ? 'pk' : 'nonPk',
                      fieldIndex: fi,
                      insertIndex: fi,
                    });
                    if (containerRef.current) containerRef.current.classList.add('rg-pk-dragging-mode');
                    return;
                  }
                  if (onAddRelationship) {
                    onFieldDragStart(e, p.entity.name, f.name, p.x + p.w, fy);
                  }
                }}
                onMouseEnter={() => {
                  if (connDragRef.current && connDragRef.current.srcEntityName !== p.entity.name) {
                    const t = { entityName: p.entity.name, fieldName: f.name };
                    connectorTargetRef.current = t;
                    setConnectorTarget(t);
                  }
                }}
                onMouseLeave={() => {
                  if (connDragRef.current) {
                    if (connectorTargetRef.current?.entityName === p.entity.name &&
                        connectorTargetRef.current?.fieldName === f.name) {
                      connectorTargetRef.current = null;
                    }
                    setConnectorTarget(prev =>
                      prev?.entityName === p.entity.name && prev?.fieldName === f.name ? null : prev
                    );
                  }
                }}
              >
                {/* field row background */}
                <rect
                  x={p.x + 1} y={fy - FIELD_H / 2}
                  width={p.w - 2} height={FIELD_H}
                  fill={isDropTarget ? 'rgba(99,102,241,.12)' : 'transparent'}
                  rx="2"
                />
                {fi > 0 && (
                  <line
                    x1={p.x + 8} y1={fy - FIELD_H / 2}
                    x2={p.x + p.w - 8} y2={fy - FIELD_H / 2}
                    stroke="var(--color-border-tertiary, #e5e7eb)"
                    strokeWidth="0.5"
                  />
                )}
                {/* connector handle dot — always shown when onAddRelationship provided */}
                {onAddRelationship && (
                  <circle
                    cx={p.x + p.w - 8} cy={fy}
                    r="5"
                    className="rg-connector-handle"
                    fill={isSource ? '#4f46e5' : '#c7d2fe'}
                    stroke={isSource ? '#4f46e5' : '#a5b4fc'}
                    strokeWidth="1"
                    style={{ cursor: 'crosshair' }}
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      e.preventDefault();
                      e.stopPropagation();
                      onFieldDragStart(e, p.entity.name, f.name, p.x + p.w, fy);
                    }}
                  />
                )}
                {/* drag grip icon */}
                {(onTogglePrimaryKey || onReorderFields) && (
                  <g className="rg-pk-grip" style={{ pointerEvents: 'none' }}>
                    <circle cx={p.x + 8} cy={fy - 2} r="1" fill="#94a3b8" />
                    <circle cx={p.x + 12} cy={fy - 2} r="1" fill="#94a3b8" />
                    <circle cx={p.x + 8} cy={fy + 2} r="1" fill="#94a3b8" />
                    <circle cx={p.x + 12} cy={fy + 2} r="1" fill="#94a3b8" />
                  </g>
                )}
                {/* PK key icon */}
                {isPK && (
                  <text
                    x={p.x + ((onTogglePrimaryKey || onReorderFields) ? 20 : 10)} y={fy}
                    dominantBaseline="central"
                    className="rg-pk-icon"
                    style={{ pointerEvents: 'none' }}
                  >
                    🔑
                  </text>
                )}
                <text
                  x={p.x + (isPK ? ((onTogglePrimaryKey || onReorderFields) ? 32 : 24) : ((onTogglePrimaryKey || onReorderFields) ? 20 : 12))} y={fy}
                  dominantBaseline="central"
                  className={`rg-field-name ${isFK ? 'rg-field-fk' : ''} ${isPK ? 'rg-field-pk' : ''}`}
                  style={{ pointerEvents: 'none' }}
                >
                  {f.name}
                </text>
                <text
                  x={p.x + p.w - 10} y={fy}
                  textAnchor="end"
                  dominantBaseline="central"
                  className="rg-field-type"
                  data-sql-type={f.sql_type || SQL_TYPES[f.type] || f.type}
                  style={{ pointerEvents: 'none' }}
                >
                  {viewMode === 'physical' ? (f.sql_type || SQL_TYPES[f.type] || f.type) : f.type}
                </text>
              </g>
            );
          };

          /* Is a pkDrag currently hovering over this entity's PK zone? */
          const pkDragOverZone = pkDrag &&
            pkDrag.entityName === p.entity.name &&
            !pkDrag.isPK &&
            pkDrag.curY >= pkZoneTop &&
            pkDrag.curY <= pkZoneBottom;

          /* Is a pkDrag of a PK field hovering BELOW the PK zone? */
          const pkDragOutZone = pkDrag &&
            pkDrag.entityName === p.entity.name &&
            pkDrag.isPK &&
            pkDrag.curY > pkZoneBottom;

          const clipId = `rg-ec-${idx}`;
          return (
            <g key={idx} className="rg-entity-group">
              <defs>
                <clipPath id={clipId}>
                  <rect x={p.x + 1} y={p.y + 1} width={p.w - 2} height={p.h - 2} rx="7" />
                </clipPath>
              </defs>
              {/* shadow */}
              <rect x={p.x + 2} y={p.y + 2} width={p.w} height={p.h}
                rx="8" fill="rgba(0,0,0,.04)" />
              {/* box */}
              <rect x={p.x} y={p.y} width={p.w} height={p.h}
                rx="8"
                fill="var(--color-background-primary, #fff)"
                stroke={color}
                strokeWidth="1.2"
              />
              {/* header bar – drag entity from here */}
              <rect
                x={p.x} y={p.y} width={p.w} height={HEADER_H}
                rx="8" fill={color}
                style={{ cursor: 'grab' }}
                onMouseDown={(e) => onEntityDragStart(e, idx)}
              />
              <rect x={p.x} y={p.y + HEADER_H - 8} width={p.w} height={8} fill={color}
                style={{ cursor: 'grab' }}
                onMouseDown={(e) => onEntityDragStart(e, idx)}
              />
              {/* entity name */}
              <text
                x={p.x + p.w / 2} y={p.y + HEADER_H / 2 + 1}
                textAnchor="middle"
                dominantBaseline="central"
                className="rg-entity-name"
                style={{ pointerEvents: 'none' }}
              >
                {p.entity.name}
              </text>

              {/* ── CLIPPED BODY (PK zone + fields) ── */}
              <g clipPath={`url(#${clipId})`}>
              {/* ── PK ZONE (virtual block) ── */}
              <rect
                x={p.x + 4} y={pkZoneTop + 2}
                width={p.w - 8} height={pzH - 4}
                fill={pkDragOverZone ? 'rgba(251,191,36,.15)' : 'rgba(251,191,36,.05)'}
                rx="6"
                stroke={pkDragOverZone ? '#f59e0b' : 'rgba(180,83,9,.18)'}
                strokeWidth={pkDragOverZone ? 1.2 : 0.7}
                strokeDasharray={pkDragOverZone ? 'none' : 'none'}
                className={`rg-pk-zone-bg ${pkDragOverZone ? 'rg-pk-zone-active' : ''}`}
              />
              {/* PK zone separator at bottom */}
              <line
                x1={p.x + 8} y1={pkZoneBottom - 2}
                x2={p.x + p.w - 8} y2={pkZoneBottom - 2}
                stroke={pkDragOverZone ? '#f59e0b' : 'var(--color-border-tertiary, #e5e7eb)'}
                strokeWidth={pkDragOverZone ? 1.5 : 0.5}
                strokeDasharray="none"
              />

              {/* PK fields inside the zone */}
              {pkFields.length > 0 ? (
                pkFields.map((f, fi) => {
                  const fy = pkZoneTop + PK_LABEL_H + fi * FIELD_H + FIELD_H / 2;
                  return renderField(f, fy, fi, 'pk');
                })
              ) : (
                <text
                  x={p.x + p.w / 2} y={pkZoneTop + PK_LABEL_H + PK_EMPTY_H / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="rg-pk-zone-hint"
                >
                  {onTogglePrimaryKey ? '↕ drag a field here' : 'no primary key'}
                </text>
              )}

              {/* ── REGULAR FIELDS ── */}
              {nonPkFields.length > 0 ? (
                nonPkFields.map((f, fi) => {
                  const fy = pkZoneBottom + fi * FIELD_H + FIELD_H / 2;
                  return renderField(f, fy, fi, 'reg');
                })
              ) : (
                <text
                  x={p.x + p.w / 2} y={pkZoneBottom + FIELD_H / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="rg-pk-zone-hint"
                >
                  all fields are primary keys
                </text>
              )}

              {/* Visual indicator: reorder drop line or cross-zone indicator */}
              {pkDrag && pkDrag.entityName === p.entity.name && (() => {
                const isReorder = pkDrag.zone === 'pk'
                  ? (pkDrag.curY >= pkZoneTop && pkDrag.curY <= pkZoneBottom)
                  : (pkDrag.curY > pkZoneBottom);
                const isCrossZone = pkDragOverZone || pkDragOutZone;

                if (isReorder && pkDrag.insertIndex !== undefined) {
                  // Snap-to reorder indicator line
                  const baseY = pkDrag.zone === 'pk'
                    ? pkZoneTop + PK_LABEL_H
                    : pkZoneBottom;
                  const indicatorY = baseY + pkDrag.insertIndex * FIELD_H;
                  const noChange = pkDrag.insertIndex === pkDrag.fieldIndex || pkDrag.insertIndex === pkDrag.fieldIndex + 1;
                  return (
                    <line
                      x1={p.x + 8} y1={indicatorY}
                      x2={p.x + p.w - 8} y2={indicatorY}
                      stroke={noChange ? '#94a3b8' : '#6366f1'}
                      strokeWidth={noChange ? 1 : 2.5}
                      strokeLinecap="round"
                      className="rg-reorder-indicator"
                    />
                  );
                }
                if (isCrossZone) {
                  // Cross-zone indicator (existing behavior)
                  return (
                    <line
                      x1={p.x + 4} y1={pkDrag.curY}
                      x2={p.x + p.w - 4} y2={pkDrag.curY}
                      stroke="#f59e0b"
                      strokeWidth="1.5"
                      strokeDasharray="4,3"
                      className="rg-pk-drag-indicator"
                    />
                  );
                }
                // Cursor-following line (fallback)
                return (
                  <line
                    x1={p.x + 4} y1={pkDrag.curY}
                    x2={p.x + p.w - 4} y2={pkDrag.curY}
                    stroke="#94a3b8"
                    strokeWidth="1"
                    strokeDasharray="4,3"
                    className="rg-pk-drag-indicator"
                  />
                );
              })()}
              </g>{/* end clip group */}
            </g>
          );
        })}

        {/* Live connector line during field drag */}
        {connectorDrag && (
          <g className="rg-connector-line">
            <path
              d={curvePath(connectorDrag.srcX, connectorDrag.srcY, connectorDrag.curX, connectorDrag.curY)}
              fill="none"
              stroke="#6366f1"
              strokeWidth="1.8"
              strokeDasharray="6,4"
              opacity="0.7"
            />
            <circle cx={connectorDrag.srcX} cy={connectorDrag.srcY} r="4" fill="#6366f1" />
            <circle cx={connectorDrag.curX} cy={connectorDrag.curY} r="4"
              fill={connectorTarget ? '#10b981' : '#6366f1'} opacity="0.6" />
          </g>
        )}

      </svg>

      {/* Toolbar: legend + download */}
      <div className="rg-toolbar">
        <div className="rg-legend">
          <div className="rg-legend-item">
            <svg width="28" height="10"><line x1="0" y1="5" x2="28" y2="5" stroke="#a5b4fc" strokeWidth="1.3" /></svg>
            <span>required</span>
          </div>
          <div className="rg-legend-item">
            <svg width="28" height="10"><line x1="0" y1="5" x2="28" y2="5" stroke="#a5b4fc" strokeWidth="1.3" strokeDasharray="5,3" /></svg>
            <span>optional</span>
          </div>
          <div className="rg-legend-item">
            <span className="rg-legend-fk">FK</span>
            <span>foreign key field</span>
          </div>
          {onAddRelationship && !onTogglePrimaryKey && (
            <div className="rg-legend-item">
              <svg width="14" height="14"><circle cx="7" cy="7" r="3" fill="#c7d2fe" stroke="#a5b4fc" strokeWidth="0.8"/></svg>
              <span>drag to connect</span>
            </div>
          )}
          {onTogglePrimaryKey && (
            <div className="rg-legend-item">
              <span style={{ fontSize: '11px' }}>↕</span>
              <span>drag field to PK zone</span>
            </div>
          )}
          {(onTogglePrimaryKey || onReorderFields) && (
            <div className="rg-legend-item">
              <svg width="14" height="14"><line x1="2" y1="7" x2="12" y2="7" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/></svg>
              <span>drag to reorder</span>
            </div>
          )}
        </div>
        <div className="rg-download-btns">
          <button className="rg-dl-btn" onClick={fitToWindow} title="Fit diagram to window">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M1 4.5V1.5h3M9 1.5h3v3M12 8.5v3h-3M4 12.5H1v-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Fit
          </button>
          <button className="rg-dl-btn" onClick={resetLayout} title="Reset layout to default">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M1.5 2v3.5h3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2.2 8.5a5 5 0 1 0 .7-4.5L1.5 5.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Reset
          </button>
          <button className="rg-dl-btn" onClick={downloadSvg} title="Download as SVG">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M6.5 1v8M3.5 6.5L6.5 9l3-2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 10.5h9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            SVG
          </button>
          <button className="rg-dl-btn" onClick={downloadPdf} title="Download as PNG">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M6.5 1v8M3.5 6.5L6.5 9l3-2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 10.5h9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            PNG
          </button>
        </div>
      </div>
    </div>
  );
}
